import { useLayoutEffect, useMemo } from 'react';
import { createRoot } from 'react-dom/client';

import { useThree } from '@react-three/fiber';
import { useEventCallback } from '@spazious/react-hooks';

export interface PortalProps {
  /**
   * The content to render inside the portal.
   */
  children: React.ReactNode;
  /**
   * Whether the portal is enabled.
   */
  enabled?: boolean;
  /**
   * The DOM element to render the portal into.
   */
  container?: Element;
  /**
   * The component to render the portal as.
   */
  as?: string;
}

/**
 * Renders a React component into a DOM node that exists outside the DOM hierarchy of the parent component.
 * Using the parent canvas view as reference for positioning.
 */
export const Portal: React.FC<PortalProps> = ({
  children,
  container = document.body as Element,
  enabled = true,
  as = 'div',
}) => {
  const target = useMemo(() => document.createElement(as), [as]);

  const { height, left, top, width } = useThree(({ size }) => size);

  const [x, y] = useMemo(() => [left + width / 2, top + height / 2], [height, left, top, width]);

  const root = useMemo(() => createRoot(target as Element), [target]);

  const mount = useEventCallback(() => {
    if (target.parentElement !== container) {
      container.appendChild(target);
    }
    // We need to check if the root has been
    // initialized before calling render
    if ((root as any)._internalRoot) {
      root.render(children);
    }
  });

  const unmount = useEventCallback((keep = false) => {
    root.unmount();
    if (target.parentElement && keep === false) {
      target.parentElement.removeChild(target);
    }
  });

  useLayoutEffect(() => {
    if (enabled) {
      mount();
    } else {
      unmount();
    }

    return unmount;
  }, [enabled, mount, unmount]);

  useLayoutEffect(() => {
    target.style.cssText = `position:absolute;top:0;left:0;transform:translate3d(${x}px,${y}px,0);transform-origin:0 0;`;
  }, [target.style, x, y]);

  return null;
};

export default Portal;
